import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Check, X, IndianRupee, Loader2, ShieldCheck, CreditCard } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const PAY_STATUS = {
  success: "bg-primary/15 text-primary border-primary/30",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
  created: "bg-warning/15 text-warning border-warning/30",
  pending: "bg-warning/15 text-warning border-warning/30",
};

export default function Billing() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [cycle, setCycle] = useState("monthly");
  const [payingKey, setPayingKey] = useState(null);
  const [page, setPage] = useState(1);

  const plans = useQuery({ queryKey: ["billing-plans"], queryFn: async () => (await api.get("/billing/plans")).data });
  const sub = useQuery({ queryKey: ["billing-sub"], queryFn: async () => (await api.get("/billing/subscription")).data });
  const payments = useQuery({ queryKey: ["billing-payments", page], queryFn: async () => (await api.get("/billing/payments", { params: { page, page_size: 10 } })).data });

  const current = sub.data?.subscription;
  const priceRank = (plans.data || []).reduce((acc, p) => ({ ...acc, [p.key]: p.price_monthly }), {});

  const labelFor = (p) => {
    if (current && current.status === "active") {
      if (current.plan_key === p.key && current.billing_cycle === cycle) return "current";
      if (current.plan_key === p.key) return "change";
      return (priceRank[p.key] > priceRank[current.plan_key]) ? "upgrade" : "downgrade";
    }
    return "subscribe";
  };

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["billing-sub"] });
    qc.invalidateQueries({ queryKey: ["billing-payments"] });
  };

  const pay = async (plan) => {
    setPayingKey(plan.key);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Failed to load Razorpay"); return; }
      const { data } = await api.post("/billing/order", { plan_key: plan.key, billing_cycle: cycle });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "MilkERP Subscription",
        description: `${data.plan_name} · ${data.billing_cycle}`,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: "#3A5A40" },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful — subscription active!");
            refetchAll();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
            refetchAll();
          }
        },
        modal: { ondismiss: () => { toast.info("Payment cancelled"); refetchAll(); } },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(`Payment failed: ${r.error?.description || "Unknown error"}`);
        refetchAll();
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment");
    } finally {
      setPayingKey(null);
    }
  };

  const cancel = useMutation({
    mutationFn: async () => (await api.post("/billing/cancel")).data,
    onSuccess: () => { toast.success("Subscription cancelled"); refetchAll(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const BTN_TEXT = { subscribe: "Subscribe", upgrade: "Upgrade", downgrade: "Downgrade", change: "Switch Cycle", current: "Current Plan" };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader title="Subscription & Billing" subtitle="Manage your center's plan and payments" breadcrumb={["Center", "Subscription"]} />

      {/* Current plan */}
      <Card data-testid="current-plan-card">
        <CardHeader><CardTitle className="font-heading text-lg">Current Plan</CardTitle></CardHeader>
        <CardContent>
          {sub.isLoading ? <Skeleton className="h-16" /> : current && current.status === "active" ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><ShieldCheck className="h-6 w-6" /></div>
                <div>
                  <p className="font-heading text-lg font-bold">{current.plan_name} · <span className="capitalize">{current.billing_cycle}</span></p>
                  <p className="text-sm text-muted-foreground">Renews on {(current.current_period_end || "").slice(0, 10)} · {money(current.amount)}</p>
                </div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" className="text-destructive" data-testid="cancel-sub-btn">Cancel Subscription</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Cancel subscription?</AlertDialogTitle><AlertDialogDescription>Your center access will move to expired until you subscribe again.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => cancel.mutate()} data-testid="confirm-cancel-sub-btn">Cancel Subscription</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="no-active-sub">No active subscription. Choose a plan below to get started.</p>
          )}
        </CardContent>
      </Card>

      {/* Cycle toggle */}
      <div className="flex justify-center">
        <Tabs value={cycle} onValueChange={setCycle}>
          <TabsList data-testid="cycle-toggle">
            <TabsTrigger value="monthly" data-testid="cycle-monthly">Monthly</TabsTrigger>
            <TabsTrigger value="yearly" data-testid="cycle-yearly">Yearly <Badge variant="secondary" className="ml-1.5">2 months free</Badge></TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Plans */}
      {plans.isLoading ? (
        <div className="grid gap-5 md:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-80" />)}</div>
      ) : (
        <div className="grid gap-5 md:grid-cols-3">
          {(plans.data || []).map((p, idx) => {
            const action = labelFor(p);
            const price = cycle === "yearly" ? p.price_yearly : p.price_monthly;
            return (
              <Card key={p.key} className={`relative overflow-hidden ${action === "current" ? "border-primary ring-1 ring-primary/30" : ""}`} data-testid={`plan-${p.key}`}>
                {action === "current" && <div className="absolute right-0 top-0 rounded-bl-lg bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Active</div>}
                <CardHeader>
                  <CardTitle className="font-heading text-xl">{p.name}</CardTitle>
                  <div className="flex items-baseline gap-1"><IndianRupee className="h-5 w-5" /><span className="font-heading text-3xl font-bold tnum">{Number(price).toLocaleString("en-IN")}</span><span className="text-sm text-muted-foreground">/{cycle === "yearly" ? "yr" : "mo"}</span></div>
                </CardHeader>
                <CardContent className="space-y-2.5 text-sm">
                  <Feature label={`${p.max_users} users`} on />
                  <Feature label={`${p.max_farmers.toLocaleString("en-IN")} farmers`} on />
                  <Feature label={`${p.max_daily_collections.toLocaleString("en-IN")} daily collections`} on />
                  <Feature label="PDF & Excel export" on={p.export_features} />
                  <Feature label="API access" on={p.api_access} />
                  <Feature label={`${p.support_priority} support`} on />
                  <Button className="mt-3 w-full" disabled={action === "current" || payingKey === p.key}
                    variant={action === "downgrade" ? "outline" : "default"}
                    onClick={() => pay(p)} data-testid={`buy-${p.key}`}>
                    {payingKey === p.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1.5 h-4 w-4" />}
                    {BTN_TEXT[action]}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Payment history */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Payment History</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Plan</TableHead><TableHead>Cycle</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Payment ID</TableHead></TableRow></TableHeader>
              <TableBody>
                {payments.isLoading
                  ? Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8" /></TableCell></TableRow>)
                  : (payments.data?.items || []).map((p) => (
                      <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                        <TableCell className="text-xs">{(p.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                        <TableCell>{p.plan_name} <Badge variant="outline" className="ml-1 capitalize">{p.action}</Badge></TableCell>
                        <TableCell className="capitalize">{p.billing_cycle}</TableCell>
                        <TableCell className="text-right tnum">{money(p.amount)}</TableCell>
                        <TableCell><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${PAY_STATUS[p.status] || ""}`}>{p.status}</span>{p.failure_reason && <p className="text-[11px] text-destructive">{p.failure_reason}</p>}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{p.razorpay_payment_id || "—"}</TableCell>
                      </TableRow>
                    ))}
                {!payments.isLoading && payments.data?.items?.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No payments yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          <TablePagination page={page} pages={payments.data?.pages} total={payments.data?.total} onPage={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}

function Feature({ label, on }) {
  return (
    <div className="flex items-center gap-2">
      {on ? <Check className="h-4 w-4 text-primary" /> : <X className="h-4 w-4 text-muted-foreground" />}
      <span className={on ? "" : "text-muted-foreground line-through"}>{label}</span>
    </div>
  );
}
